// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'

// Module Imports
import {
  PERIODIC_AVAILABLE_DRAFTS_KEY,
  PERIODIC_LIST_KEY,
} from '@/modules/periodic-generation/periodic-generation.constants'
import {
  activatePeriodic,
  createPeriodic,
  deletePeriodic,
  getAvailableDrafts,
  getPeriodicDetail,
  getPeriodicList,
  passivePeriodic,
  updatePeriodic,
} from '@/modules/periodic-generation/periodic-generation.service'
import type {
  TCreatePeriodicRequest,
  TPeriodicGeneration,
  TUpdatePeriodicRequest,
} from '@/modules/periodic-generation/periodic-generation.types'

export const periodicListQueryOptions = (
  page: number,
  query: string,
  status?: string
) =>
  queryOptions({
    queryKey: [...PERIODIC_LIST_KEY, page, query, status ?? 'ALL'],
    queryFn: () => getPeriodicList(page, query, status),
  })

export const periodicDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['periodicGenerationDetail', id],
    queryFn: () => getPeriodicDetail(id),
    staleTime: 0,
  })

export const availableDraftsQueryOptions = (includeId?: string) =>
  queryOptions({
    queryKey: [...PERIODIC_AVAILABLE_DRAFTS_KEY, includeId ?? ''],
    queryFn: () => getAvailableDrafts(includeId),
    staleTime: 0,
  })

export function useCreatePeriodic({
  onSuccess,
  onError,
}: TMutation<TPeriodicGeneration, TResponseError, TCreatePeriodicRequest>) {
  return useMutation<
    TPeriodicGeneration,
    TResponseError,
    TCreatePeriodicRequest
  >({ mutationFn: createPeriodic, onSuccess, onError })
}

export function useUpdatePeriodic({
  onSuccess,
  onError,
}: TMutation<
  TPeriodicGeneration,
  TResponseError,
  { id: string; body: TUpdatePeriodicRequest }
>) {
  return useMutation<
    TPeriodicGeneration,
    TResponseError,
    { id: string; body: TUpdatePeriodicRequest }
  >({
    mutationFn: ({ id, body }) => updatePeriodic(id, body),
    onSuccess,
    onError,
  })
}

export function useActivatePeriodic({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: activatePeriodic,
    onSuccess,
    onError,
  })
}

export function usePassivePeriodic({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: passivePeriodic,
    onSuccess,
    onError,
  })
}

export function useDeletePeriodic({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deletePeriodic,
    onSuccess,
    onError,
  })
}
